import {
  createQlikConnectionInputSchema,
  listOrganizationMembershipsResponseSchema,
  updateQlikConnectionInputSchema,
  listQlikConnectionsResponseSchema,
  listQlikAppsResponseSchema,
  qlikAppSchema,
  listQlikVariablesResponseSchema,
  listQlikDimensionsResponseSchema,
  listQlikFieldsResponseSchema,
  listQlikMeasuresResponseSchema,
  qlikConnectionSchema,
  qlikAppMetadataCacheResponseSchema,
  qlikAppMetadataCacheOptionalResponseSchema,
  testQlikConnectionResponseSchema,
  toStandardError,
  type TestQlikConnectionResponse,
  type CreateQlikConnectionInput,
  type UpdateQlikConnectionInput,
  type QlikConnection,
  type QlikApp,
  type QlikVariable,
  type QlikDimension,
  type QlikField,
  type QlikMeasure,
  type UpdateQlikDimensionInput,
  type UpdateQlikMeasureInput,
  type OrganizationMembership,
  type QlikAppMetadataCache,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequest, apiRequestData, apiRequestJson } from "../api-client";

export async function listQlikConnections(): Promise<QlikConnection[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections`,
    responseSchema: listQlikConnectionsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function listOrganizationMemberships(): Promise<OrganizationMembership[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/organizations`,
    responseSchema: listOrganizationMembershipsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function createQlikConnection(
  input: CreateQlikConnectionInput,
): Promise<QlikConnection> {
  const validated = createQlikConnectionInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections`,
    method: "POST",
    body: validated.data,
    dataSchema: qlikConnectionSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function getQlikConnection(id: string): Promise<QlikConnection> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections/${id}`,
    method: "GET",
    dataSchema: qlikConnectionSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function deleteQlikConnection(id: string): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${id}`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

export async function testQlikConnection(id: string): Promise<TestQlikConnectionResponse["data"]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${id}/test`,
    method: "POST",
    responseSchema: testQlikConnectionResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });
  return response.data;
}

export async function updateQlikConnection(
  id: string,
  input: UpdateQlikConnectionInput,
): Promise<QlikConnection> {
  const validated = updateQlikConnectionInputSchema.safeParse(input);
  if (!validated.success) {
    throw toStandardError({
      code: "VALIDATION_ERROR",
      message: "Invalid input",
      status: 400,
      details: validated.error.flatten(),
    });
  }

  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections/${id}`,
    method: "PATCH",
    body: validated.data,
    dataSchema: qlikConnectionSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function listAppsFromConnection(connectionId: string): Promise<QlikApp[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps`,
    responseSchema: listQlikAppsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function getAppFromConnection(connectionId: string, appId: string): Promise<QlikApp> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}`,
    method: "GET",
    dataSchema: qlikAppSchema,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function listVariablesFromApp(
  connectionId: string,
  appId: string,
): Promise<QlikVariable[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/variables`,
    responseSchema: listQlikVariablesResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function updateVariableInApp(
  connectionId: string,
  appId: string,
  variableId: string,
  data: { name?: string; definition?: string; comment?: string },
): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/variables/${variableId}`,
    method: "PUT",
    body: data,
    expectedStatus: 204,
  });
}

export async function listDimensionsFromApp(
  connectionId: string,
  appId: string,
): Promise<QlikDimension[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/dimensions`,
    responseSchema: listQlikDimensionsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function updateDimensionInApp(
  connectionId: string,
  appId: string,
  dimensionId: string,
  data: UpdateQlikDimensionInput,
): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/dimensions/${dimensionId}`,
    method: "PUT",
    body: data,
    expectedStatus: 204,
  });
}

export async function listFieldsFromApp(connectionId: string, appId: string): Promise<QlikField[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/fields`,
    responseSchema: listQlikFieldsResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function listMeasuresFromApp(
  connectionId: string,
  appId: string,
): Promise<QlikMeasure[]> {
  const response = await apiRequestJson({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/measures`,
    responseSchema: listQlikMeasuresResponseSchema,
    badResponseMessage: "Unexpected response from server",
  });

  return response.data;
}

export async function updateMeasureInApp(
  connectionId: string,
  appId: string,
  measureId: string,
  data: UpdateQlikMeasureInput,
): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/measures/${measureId}`,
    method: "PUT",
    body: data,
    expectedStatus: 204,
  });
}

export async function getAppMetadataCache(
  connectionId: string,
  appId: string,
): Promise<QlikAppMetadataCache | null> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/metadata`,
    method: "GET",
    dataSchema: qlikAppMetadataCacheOptionalResponseSchema.shape.data,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function refreshAppMetadata(
  connectionId: string,
  appId: string,
): Promise<QlikAppMetadataCache> {
  return apiRequestData({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/metadata/refresh`,
    method: "POST",
    dataSchema: qlikAppMetadataCacheResponseSchema.shape.data,
    badResponseMessage: "Unexpected response from server",
  });
}

export async function invalidateAppMetadataCache(
  connectionId: string,
  appId: string,
): Promise<void> {
  await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/${appId}/metadata`,
    method: "DELETE",
    expectedStatus: 204,
  });
}

export async function refreshAllAppsMetadata(
  connectionId: string,
): Promise<{ appId: string; success: boolean; error?: string }[]> {
  const { payload } = await apiRequest({
    path: `${API_V1_PREFIX}/qlik/connections/${connectionId}/apps/metadata/refresh-all`,
    method: "POST",
  });

  const raw = payload as { data?: { appId: string; success: boolean; error?: string }[] };
  return raw?.data ?? [];
}

export type {
  QlikVariable,
  QlikDimension,
  QlikField,
  QlikMeasure,
  OrganizationMembership,
  QlikAppMetadataCache,
};
